import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {NavBar} from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import ComplianceAIForm from "@/components/compliance-ai/ComplianceAIForm";
import ComplianceAIResponse from "@/components/compliance-ai/ComplianceAIResponse";
import { ComplianceFormData } from "@/types/compliance";
import { mockComplianceAiResponse } from "@/mocks/complianceAiResponse";
import { API_CONFIG } from "@/constants/env.ts";
import { CheckCircle, Clock, FileCheck, BarChart3, Shield, Briefcase, Brain } from "lucide-react";

const FinanceMarketingLanding = () => {
  const [formData, setFormData] = useState<ComplianceFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [response, setResponse] = useState<any | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(60); // For loading UI if needed

  const handleFormSubmit = async (data: ComplianceFormData) => {
    setFormData(data);
    setIsLoading(true);
    setError(null);

    try {
      // Use the actual API
      const apiUrl = `${window.location.origin}/compliance-ai`;
      console.log("Sending request to:", apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
      }
      
      let responseData = await response.json();
      
      console.log("API Response:", responseData);
      
      // Process the response if needed
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData);
        } catch (e) {
          responseData = { content: responseData };
        }
      }
      
      setResponse(responseData);
    } catch (error) {
      console.error('Error submitting form:', error);
      
      if (error instanceof Error) {
        console.log('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
        setError(error);
      } else {
        setError(new Error('An unknown error occurred'));
      }
      
      // Fallback to mock data if API fails
      if (API_CONFIG.USE_MOCK_API) {
        console.log("Falling back to mock data due to API error");
        setTimeout(() => {
          setResponse({
            ...mockComplianceAiResponse,
            title: "Financial Marketing Compliance Assessment",
            summary: "Your financial marketing materials have been analyzed for regulatory compliance. Here are our recommendations to improve your compliance posture."
          });
        }, 1000);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="relative min-h-screen overflow-hidden">
      <Helmet>
        <title>AI-Powered Financial Marketing Compliance | Verdea AI</title>
        <meta 
          name="description" 
          content="Transform your financial marketing compliance with AI. Streamline pre-approval reviews and ensure regulatory compliance across all marketing channels." 
        />
      </Helmet>
      
      {/* Background Elements */}
      <div className="fixed inset-0 z-0 pointer-events-none bg-gray-50">
        <div
          className="absolute top-0 right-0 w-[500px] h-[500px] blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(27, 101, 4, 0.4) 0%, rgba(27, 101, 4, 0) 70%)',
            transform: 'translate(30%, -30%)'
          }}
        ></div>
        <div
          className="absolute bottom-0 left-0 w-[500px] h-[500px] blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(27, 101, 4, 0.3) 0%, rgba(27, 101, 4, 0) 70%)',
            transform: 'translate(-30%, 30%)'
          }}
        ></div>
      </div>
      
      {/* Navbar - landing mode hides platform, tools and login buttons */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm">
        <NavBar landingMode={true} />
      </header>
      
      <main className="relative z-10 pt-20">
        {/* Main content with form section */}
        <section className="py-12 pb-16 bg-transparent">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto text-center mb-8">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 tracking-tight">
                <span className="block">Financial Marketing Compliance</span>
                <span className="bg-gradient-to-r from-[#184c06] via-[#1b6504] to-[#229302] bg-clip-text text-transparent">Powered by AI</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
                Streamline compliance reviews, accelerate time-to-market, and mitigate regulatory risks with our modular AI regulatory technology platform for financial institutions.
              </p>
              <div className="flex flex-wrap justify-center items-center gap-4 mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="h-5 w-5 text-[#1b6504]" />
                  <span>Free Compliance Analysis</span>
                </div>
                <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="h-5 w-5 text-[#1b6504]" />
                  <span>90% Faster Reviews</span>
                </div>
                <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <FileCheck className="h-5 w-5 text-[#1b6504]" />
                  <span>Multi-Regulatory Coverage</span>
                </div>
              </div>
            </div>
            
            {/* Form Section */}
            <div className="max-w-5xl mx-auto mb-16">
              <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-200/80">
                {!response ? (
                  <>
                    <h2 className="text-2xl font-bold mb-6 text-center">Tell us about your marketing compliance challenges</h2>
                    <ComplianceAIForm 
                      onSubmit={handleFormSubmit} 
                      isLoading={isLoading}
                      error={error}
                    />
                  </>
                ) : (
                  <ComplianceAIResponse 
                    response={response} 
                    isLoading={isLoading} 
                    error={error}
                    onReset={() => {
                      setFormData(null);
                      setResponse(null);
                      setError(null);
                    }} 
                  />
                )}
              </div>
            </div>
          </div>
        </section>
        
        {/* Regulations Coverage Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Comprehensive Regulatory Coverage</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Our platform helps financial institutions maintain compliance across multiple regulatory frameworks and jurisdictions.
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {[
                  { name: "AIFMD", description: "Alternative Investment Fund Managers Directive" },
                  { name: "MiFID II", description: "Markets in Financial Instruments Directive" },
                  { name: "FINRA", description: "Financial Industry Regulatory Authority" },
                  { name: "SEC", description: "Securities and Exchange Commission" },
                  { name: "FCA", description: "Financial Conduct Authority" },
                  { name: "ESMA", description: "European Securities and Markets Authority" },
                  { name: "GDPR", description: "General Data Protection Regulation" },
                  { name: "CCPA/CPRA", description: "California Consumer Privacy Act" }
                ].map((regulation, index) => (
                  <div
                    key={index}
                    className="bg-green-50 p-4 rounded-xl border border-green-100"
                  >
                    <h3 className="font-bold text-[#1b6504] mb-1">{regulation.name}</h3>
                    <p className="text-sm text-gray-600">{regulation.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        {/* How Verdea AI helps section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">AI-Powered Marketing Compliance</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Our platform transforms how financial institutions handle marketing compliance, saving time while reducing risk.
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  {
                    title: "Automated Pre-Approval",
                    description: "Our AI analyzes financial marketing content in seconds, identifying compliance issues and suggesting fixes before materials go to review.",
                    icon: <Brain className="h-10 w-10 text-[#1b6504] mb-4" />
                  },
                  {
                    title: "Risk Detection",
                    description: "Identify potential compliance risks across all marketing channels, from web content to social media and promotional materials.",
                    icon: <Shield className="h-10 w-10 text-[#1b6504] mb-4" />
                  },
                  {
                    title: "Streamlined Workflow",
                    description: "Reduce review times by up to 90%, allowing your marketing team to move quickly while maintaining regulatory compliance.",
                    icon: <BarChart3 className="h-10 w-10 text-[#1b6504] mb-4" />
                  }
                ].map((feature, index) => (
                  <div
                    key={index}
                    className="bg-white p-6 rounded-xl shadow-md"
                  >
                    {feature.icon}
                    <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        
        {/* Features & Benefits section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Key Benefits</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Why financial institutions choose our AI-powered marketing compliance platform
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  {
                    title: "90% Faster Compliance Reviews",
                    description: "Reduce review times from days to minutes with AI-powered content analysis that identifies issues before they reach compliance teams.",
                    icon: <Clock className="h-6 w-6 text-[#1b6504]" />
                  },
                  {
                    title: "Reduced Compliance Risk",
                    description: "Our AI identifies subtle regulatory issues that human reviewers might miss, helping you avoid costly compliance violations.",
                    icon: <Shield className="h-6 w-6 text-[#1b6504]" />
                  },
                  {
                    title: "Multi-Channel Coverage",
                    description: "Monitor compliance across all marketing channels - websites, social media, email campaigns, and promotional materials.",
                    icon: <FileCheck className="h-6 w-6 text-[#1b6504]" />
                  },
                  {
                    title: "Industry-Specific Knowledge",
                    description: "Our AI is trained on financial regulations and industry best practices to provide tailored compliance guidance.",
                    icon: <Briefcase className="h-6 w-6 text-[#1b6504]" />
                  }
                ].map((benefit, index) => (
                  <div
                    key={index}
                    className="bg-white p-6 rounded-xl flex items-start gap-4"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                      {benefit.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQ section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Common questions about our financial marketing compliance platform
                </p>
              </div>
              
              <div className="space-y-4">
                {[
                  {
                    question: "How accurate is the AI compliance analysis?",
                    answer: "Our AI achieves a 90% accuracy rate for financial marketing compliance analysis, comparable to expert human reviewers but delivered in a fraction of the time."
                  },
                  {
                    question: "What regulatory frameworks does your platform cover?",
                    answer: "Our platform covers major financial regulations including AIFMD, MiFID II, FINRA, SEC requirements, FCA guidelines, ESMA directives, and more. We continuously update our AI with the latest regulatory changes."
                  },
                  {
                    question: "How does the platform integrate with our existing workflows?",
                    answer: "Verdea AI offers seamless integration with popular marketing tools, CMS platforms, and compliance management systems. Our API allows for custom integrations with your existing tech stack."
                  },
                  {
                    question: "How do you handle data security and privacy?",
                    answer: "We maintain the highest standards of data security with GDPR compliance, end-to-end encryption, and secure cloud infrastructure. Your sensitive financial information never leaves our secure environment."
                  }
                ].map((faq, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 p-6 rounded-xl shadow-sm"
                  >
                    <h3 className="text-xl font-bold mb-2">{faq.question}</h3>
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        {/* Final CTA */}
        <section className="py-12 bg-[#1b6504] text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-4">Transform Your Financial Marketing Compliance</h2>
              <p className="text-xl opacity-90 mb-8 max-w-3xl mx-auto">
                Get your free compliance analysis today and discover how AI can help your team achieve faster, more accurate regulatory compliance.
              </p>
              <Button 
                size="lg" 
                className="bg-white text-[#1b6504] hover:bg-gray-100 rounded-full px-8"
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Get Your Free Analysis
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      {/* Footer */}
      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default FinanceMarketingLanding;